import React from 'react';
import cx from 'classnames';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import config from '@plone/volto/registry';
import ViewDefaultBlock from '@plone/volto/components/manage/Blocks/Block/DefaultView';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import {
  applyBlockDefaults,
  buildStyleClassNamesFromData,
  buildStyleObjectFromData,
  getBlocks,
  hasBlocksData,
} from '@plone/volto/helpers/Blocks/Blocks';

import { type InternalContent } from './types';
import { type VoltoBlock } from '../News/types';

interface RenderBlocksWithoutTitleProps {
  content: InternalContent;
}

// Verifica se um bloco não deve ser considerado como conteúdo real da página
const isBlockEmpty = (block?: VoltoBlock): boolean => {
  if (!block) return true;
  if (block['@type'] === 'title') return true;
  // Blocos de texto criados em branco (padrão do editor) não têm plaintext
  if (
    (block['@type'] === 'slate' || block['@type'] === 'text') &&
    !block.plaintext?.trim()
  ) {
    return true;
  }
  return false;
};

/**
 * Renderiza os blocos da página, cada um dentro do seu próprio `.mpf-bloco`.
 *
 * **Por que não usar o `RenderBlocks` do core.** Em modo de leitura ele não
 * põe wrapper nenhum em volta de cada bloco: o `StyleWrapper` faz
 * `cloneElement` e injeta `className` como PROP no componente do bloco, e as
 * views do addon não repassam essa prop — a classe nem chega ao DOM. Sem um nó
 * por bloco não há item de grade, e o campo "Largura" (ver
 * `config/blocks/enhancers/largura.ts`) não teria onde agir. O mesmo buraco já
 * estava anotado em `Blocks/SubHomeText/withSubHomeCard.tsx`.
 *
 * No EDITOR o problema não existe: o `EditBlockWrapper` do core põe as mesmas
 * classes direto no `.block-editor-*`. Por isso este wrapper usa
 * `buildStyleClassNamesFromData`, o mesmo helper — para que leitura e edição
 * produzam exatamente a mesma classe e o CSS seja um só.
 *
 * O que se perde em relação ao core: `blockWrapperTag` e `isContainer`, que
 * nenhuma das cinco views que usam este componente (`InternalView`,
 * `SubHomeView`, `FolderView`, `LinksView`, `FilesView`) precisa.
 */
export const RenderBlocksWithoutTitle = ({
  content,
  ...rest
}: RenderBlocksWithoutTitleProps): React.JSX.Element | null => {
  const intl = useIntl();
  const location = useLocation();
  const blocksConfig = config.blocks.blocksConfig;

  // Filtra os itens do layout, removendo blocos sem conteúdo real (title, texto vazio, etc.)
  const filteredItems =
    content.blocks_layout?.items?.filter(
      (id) => !isBlockEmpty(content.blocks?.[id]),
    ) || [];

  // Cria um clone do content injetando apenas o layout filtrado
  const filteredContent = {
    ...content,
    blocks_layout: {
      ...content.blocks_layout,
      items: filteredItems,
    },
  };

  if (!hasBlocksData(filteredContent) || filteredItems.length === 0) {
    return null;
  }

  return (
    <div className="mpf-internal-view__body mpf-internal-view__editor-content">
      {getBlocks(filteredContent).map(([id, rawBlockData]) => {
        const Block =
          blocksConfig[rawBlockData?.['@type']]?.view || ViewDefaultBlock;

        const data = applyBlockDefaults({
          data: rawBlockData,
          intl,
          metadata: (rest as any).metadata,
          properties: filteredContent,
        });

        return (
          <div
            key={id}
            className={cx(
              'mpf-bloco',
              buildStyleClassNamesFromData(data.styles),
            )}
            style={buildStyleObjectFromData(data)}
          >
            <Block
              id={id}
              metadata={(rest as any).metadata}
              properties={filteredContent}
              data={data}
              path={getBaseUrl(location?.pathname || '')}
              blocksConfig={blocksConfig}
            />
          </div>
        );
      })}
    </div>
  );
};
